import FilterListIcon from "@mui/icons-material/FilterList";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { MRT_ShowHideColumnsMenu, type MRT_RowData, type MRT_TableInstance } from "material-react-table";
import { useContext, useState, type MouseEvent } from "react";
import { TableCardSortAndFilter } from "_shared/_views/tables/table-cards";
import { ReaderColumnChoiceContext } from "_shared/_views/tables/table-column-preferences-context";
import { canFilterSomeColumn } from "_shared/_views/tables/table-toolbar";

/**
 * How many columns this table is not showing because the reader put them away.
 *
 * Only the reader's own choice counts. A column the table hides by default, or that the page forces
 * off — Hire date outside Archived, a phone layout — is not one the reader has to remember, so it
 * never lights the badge. And a choice about a column the table no longer has counts for nothing.
 */
export function countColumnsTheReaderHid<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  choice: Readonly<Record<string, boolean>> | undefined,
): number {
  if (!choice) return 0;
  return table.getAllLeafColumns().filter((column) => choice[column.id] === false && !column.getIsVisible()).length;
}

/**
 * A table's own tools -- column filters, the columns to show, full screen -- behind one button.
 *
 * Each was a button of its own beside the search box, three controls a reader rarely needs that
 * crowded the ones they use every visit (#6473). Every list and collection table draws this one
 * button, so there is one Show/hide implementation (#6712). Column filters is left out of a table
 * none of whose columns can be filtered, where it would open an empty row (#5453). The dot the Show/Hide button carried while the
 * reader had columns put away stays, on this button, so a column hidden weeks ago is still easy to
 * find.
 */
export function TableToolsMenu<TData extends MRT_RowData>(
  props: Readonly<{
    table: MRT_TableInstance<TData>;
    /**
     * False for a table drawn in a dialog the reader can already take full screen: two Full screen
     * controls on one surface is a reader pressing the wrong one.
     */
    offersFullScreen?: boolean;
  }>,
) {
  // The table instance is one mutable object that MRT edits in place, so it is the same prop on
  // every render. Memoized, the column list below was kept as the element first drawn: flipping a
  // switch hid the column but left the switch where it was, until the list was closed and opened
  // again (#6599). The shared table wrapper and the empty state carry the same directive.
  "use no memo";

  const { table, offersFullScreen = true } = props;
  // The button the menu opened from, held while it is open: the column list opens from the same place.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const choice = useContext(ReaderColumnChoiceContext);
  const hiddenByReader = countColumnsTheReaderHid(table, choice);
  const { showColumnFilters, isFullScreen } = table.getState();
  const canFilter = canFilterSomeColumn(table);

  function openMenu(event: MouseEvent<HTMLElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function toggleColumnFilters() {
    setMenuAnchor(null);
    table.setShowColumnFilters(!showColumnFilters);
  }

  function openColumns() {
    setColumnsAnchor(menuAnchor);
    setMenuAnchor(null);
  }

  function toggleFullScreen() {
    setMenuAnchor(null);
    table.setIsFullScreen(!isFullScreen);
  }

  return (
    <>
      {/* A card list's Sort and Filter, in the search row before ⋯. Nothing outside a card list. */}
      <TableCardSortAndFilter table={table} />
      <Tooltip title="Table tools">
        <IconButton
          aria-label="Table tools"
          aria-haspopup="menu"
          aria-expanded={menuAnchor !== null}
          onClick={openMenu}
        >
          <Badge color="primary" invisible={hiddenByReader === 0} overlap="circular" variant="dot">
            <MoreHorizIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
        {canFilter && (
          <MenuItem onClick={toggleColumnFilters}>
            <ListItemIcon><FilterListIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{showColumnFilters ? "Hide column filters" : "Column filters"}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={openColumns}>
          <ListItemIcon><ViewColumnIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            secondary={hiddenByReader > 0
              ? `${hiddenByReader} ${hiddenByReader === 1 ? "column" : "columns"} hidden by you`
              : undefined}
          >
            Show / hide columns
          </ListItemText>
        </MenuItem>
        {offersFullScreen && (
          <MenuItem onClick={toggleFullScreen}>
            <ListItemIcon>
              {isFullScreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{isFullScreen ? "Exit full screen" : "Full screen"}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      {columnsAnchor && (
        <MRT_ShowHideColumnsMenu anchorEl={columnsAnchor} setAnchorEl={setColumnsAnchor} table={table} />
      )}
    </>
  );
}
